import { Box, ButtonBase, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DayPicker,
  type DayButtonProps,
  type Formatters,
  type Labels,
  type Matcher,
} from 'react-day-picker';
import {
  compareIso,
  dateToIso,
  isWithinBounds,
  isoToDate,
  type IsoDate,
  type IsoDateRange,
} from './isoDate';
import {
  clampMonth,
  fromMonthIndex,
  isoMonth,
  monthLimits,
  movePanel,
  panelLimits,
  toMonthIndex,
  withinLimits,
  type MonthIndex,
  type MonthLimits,
} from './panels';
import type { Bound } from './rangeDraft';

/**
 * THE GRID — the platform calendar spec's month panels, on react-day-picker v10, drawn in
 * the Figma `calendar` component set's metrics (`26801:159900`; one month `Variant3`
 * `40245:261809`).
 *
 * This file is the ONLY importer of the engine (an ESLint `no-restricted-imports` rule bans
 * it everywhere else), so swapping the engine means rewriting one file, not every screen.
 * The grid owns no value: it draws what it is given and reports the day that was clicked.
 * What a click MEANS — which end of a range it sets, whether the popover closes — is the
 * field's decision (`rangeDraft.ts`).
 *
 * ONE ENGINE INSTANCE PER PANEL. A range shows two panels that move independently (spec
 * *Navigation*): each has its own « ‹ Month Year › », and its month and year names open a
 * month or year picker for that panel alone. The right panel always shows a later month
 * than the left; a move that would break that pushes the other panel along (`movePanel`).
 * The keyboard walks the days of its panel; crossing the month edge moves that panel.
 *
 * Monday first, six rows always. Today is outlined. Days of the neighbouring months are
 * shown faded and are not days of this panel: they can't be clicked and carry no range
 * fill (spec *Structure*, user ruling 2026-11-02).
 */

export type CalendarGridSelection =
  | { mode: 'single'; value: IsoDate | null }
  | {
      mode: 'range';
      value: IsoDateRange;
      /** The range has a start and deliberately no end: the fill runs on from the start. */
      openEnded?: boolean;
      /**
       * Which end the next click sets. While it is the END and a start exists, the days
       * from the start up to the hovered or focused day preview the range.
       */
      activeBound?: Bound;
    };

export type CalendarGridProps = CalendarGridSelection & {
  /** The day the auditor clicked (or chose with Enter/Space). */
  onPick: (day: IsoDate) => void;
  /** The months shown — one panel per entry: one for a single date, left and right for a range. */
  months: MonthIndex[];
  onMonthsChange: (months: MonthIndex[]) => void;
  /** Earliest pickable day; navigation stops at its month. */
  minDate?: IsoDate | null;
  /** Latest pickable day; navigation stops at its month. */
  maxDate?: IsoDate | null;
  /** The panel whose day takes focus on mount, if any (the keyboard route into the grid). */
  autoFocusPanel?: number | null;
  'data-testid'?: string;
};

/** `de` reads `de-CH` (Swiss dates and weekdays); anything else reads British English. */
export function intlLocale(language: string | undefined): string {
  return (language ?? '').toLowerCase().startsWith('de') ? 'de-CH' : 'en-GB';
}

const DAY = 32;
/** The grid's side margin, and the header's side padding above it. */
const INSET = 8;
/** One month: seven days and the two insets — 240px, as `Variant3` draws it. */
const MONTH_WIDTH = 7 * DAY + 2 * INSET;
/** The day grid's height (weekday row + six weeks, 2px between rows, 6px under): pickers match it. */
const BODY_HEIGHT = 7 * DAY + 8 * 2 + 6;

const Panels = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  // The rule between the two panels runs from the header down through the grid.
  '& > [data-panel] + [data-panel]': { borderLeft: `1px solid ${theme.palette.divider}` },
}));

const PanelBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: MONTH_WIDTH,
  color: theme.palette.text.secondary,
  fontFamily: theme.typography.fontFamily,
  fontVariantNumeric: 'lining-nums proportional-nums',

  '& .rdp-month_grid': {
    borderCollapse: 'separate',
    // 2px between rows (Figma `gap: 2`); none between days, so a range reads as one band.
    borderSpacing: '0 2px',
    margin: `0 ${INSET}px 6px`,
  },
  '& .rdp-weekday': {
    width: DAY,
    height: DAY,
    padding: 0,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.4,
    textAlign: 'center',
    color: theme.palette.text.label,
  },
  '& .rdp-day': {
    width: DAY,
    height: DAY,
    padding: 0,
    textAlign: 'center',
  },
  '& .rdp-day_button': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: DAY,
    height: DAY,
    margin: 0,
    padding: 0,
    border: 0,
    borderRadius: 8,
    background: 'none',
    font: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
    color: 'inherit',
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.background.default },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
    '&:disabled, &[aria-disabled="true"]': {
      cursor: 'not-allowed',
      backgroundColor: 'transparent',
    },
  },
  '& .rdp-disabled .rdp-day_button': { color: theme.palette.text.disabled },

  // Today: a light outline (spec *Structure*).
  '& .rdp-today .rdp-day_button': {
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },

  // The band between the ends, and the preview of one while the end is being chosen.
  '& .cal-band .rdp-day_button, & .cal-preview .rdp-day_button': {
    borderRadius: 0,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    '&:hover': { backgroundColor: theme.palette.divider },
  },
  // The day the pointer or focus would end the range on.
  '& .cal-preview-end .rdp-day_button': {
    boxShadow: `inset 0 0 0 1px ${theme.palette.text.primary}`,
  },

  /* A picked day and a range's two ends: the dark cap, white ink — a filled box, so the
     ends do not rely on colour alone. Rounded on the outer side only, so it joins the band
     (Figma: 8 0 0 8); a lone day is round. */
  '& .cal-cap-start .rdp-day_button, & .cal-cap-end .rdp-day_button': {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.common.white,
    '&:hover': { backgroundColor: theme.palette.text.secondary },
  },
  '& .cal-cap-start .rdp-day_button': { borderRadius: '8px 0 0 8px' },
  '& .cal-cap-end .rdp-day_button': { borderRadius: '0 8px 8px 0' },
  '& .cal-cap-round .rdp-day_button': { borderRadius: 8 },

  /* Days of the neighbouring months are not this panel's: faded, not clickable, and never
     part of a range's fill — whatever the modifiers above say about their date. */
  '& .rdp-day.rdp-outside .rdp-day_button': {
    color: theme.palette.text.placeholder,
    opacity: 0.6,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderRadius: 8,
    cursor: 'default',
  },
}));

/**
 * The header: « ‹ at the left edge, › » at the right, the month and year names between.
 * ONE WIDTH IN EVERY MONTH (#986): the header is the month's width and its nav pairs sit
 * at the edges, so a long month name never moves an arrow from under the pointer.
 */
const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  width: MONTH_WIDTH,
  height: 42,
  padding: `0 ${INSET}px`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Nav = styled(Box)({ display: 'flex', alignItems: 'center', flexShrink: 0 });

/** Figma `buttons gray ghost (tr)` icon-only, in the chevrons' #A0B7BA. */
const NavButton = styled(IconButton)(({ theme }) => ({
  padding: 3,
  borderRadius: 6,
  color: theme.palette.grey[80],
  '&:hover': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.tertiary,
  },
  '&.Mui-disabled': { color: theme.palette.text.disabled },
  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
}));

/** Takes what the arrows leave (136px); should a name still outgrow it, it clips. */
const Title = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  justifyContent: 'center',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

const TitleButton = styled(ButtonBase)(({ theme }) => ({
  padding: '2px 4px',
  borderRadius: 4,
  fontFamily: theme.typography.fontFamily,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.4,
  color: theme.palette.text.primary,
  '&.calendar-year': { color: theme.palette.text.label },
  '&:hover': { backgroundColor: theme.palette.background.default },
  '&[aria-expanded="true"]': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.primary.main,
  },
  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
}));

const PickerBody = styled(Box)({
  boxSizing: 'border-box',
  height: BODY_HEIGHT,
  padding: `12px ${INSET + 4}px 8px`,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const PickerGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 6,
});

const PickerButton = styled(ButtonBase)(({ theme }) => ({
  height: 36,
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  fontFamily: theme.typography.fontFamily,
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  '&:hover': { backgroundColor: theme.palette.background.default },
  '&[aria-current="true"]': {
    backgroundColor: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
    color: theme.palette.common.white,
  },
  '&.Mui-disabled': { color: theme.palette.text.disabled },
  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
}));

const PickerPaging = styled(Box)({ display: 'flex', justifyContent: 'space-between' });

const PagingButton = styled(ButtonBase)(({ theme }) => ({
  padding: '4px 6px',
  borderRadius: 4,
  fontFamily: theme.typography.fontFamily,
  fontSize: 12,
  fontWeight: 600,
  color: theme.palette.primary.main,
  '&.Mui-disabled': { color: theme.palette.text.disabled },
  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
}));

type NavStep = 'prevYear' | 'prevMonth' | 'nextMonth' | 'nextYear';

const NAV_STEP: Record<NavStep, number> = {
  prevYear: -12,
  prevMonth: -1,
  nextMonth: 1,
  nextYear: 12,
};
const NAV_ICON = {
  prevYear: ChevronsLeft,
  prevMonth: ChevronLeft,
  nextMonth: ChevronRight,
  nextYear: ChevronsRight,
} as const;
const NAV_LABEL_KEY = {
  prevYear: 'calendar_previous_year',
  prevMonth: 'calendar_previous_month',
  nextMonth: 'calendar_next_month',
  nextYear: 'calendar_next_year',
} as const;

type PickerView = 'month' | 'year' | null;

/** A day `months` months on, kept in its month (31 March → 30 April). */
function addMonthsKeepingDay(date: Date, months: number): Date {
  const first = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return new Date(first.getFullYear(), first.getMonth(), Math.min(date.getDate(), last));
}

/**
 * Where the engine's keyboard map would move focus from `from` (react-day-picker v10:
 * arrows by a day or a week — with Shift a month or a year —, Page Up/Down by a month —
 * with Shift a year —, Home/End to the week's Monday or Sunday). `null` for other keys.
 */
function keyboardTarget(from: IsoDate, key: string, shift: boolean): IsoDate | null {
  const date = isoToDate(from);
  if (!date) return null;
  const days = (n: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
  const weekday = (date.getDay() + 6) % 7;
  const next: Record<string, () => Date> = {
    ArrowLeft: () => (shift ? addMonthsKeepingDay(date, -1) : days(-1)),
    ArrowRight: () => (shift ? addMonthsKeepingDay(date, 1) : days(1)),
    ArrowUp: () => (shift ? addMonthsKeepingDay(date, -12) : days(-7)),
    ArrowDown: () => (shift ? addMonthsKeepingDay(date, 12) : days(7)),
    PageUp: () => addMonthsKeepingDay(date, shift ? -12 : -1),
    PageDown: () => addMonthsKeepingDay(date, shift ? 12 : 1),
    Home: () => days(-weekday),
    End: () => days(6 - weekday),
  };
  return next[key] ? dateToIso(next[key]()) : null;
}

/**
 * Whether focus is inside THIS panel's grid. The engine focuses the day it considers
 * focused on every render; that is right while the keyboard walks the grid, and wrong
 * after a pointer pick made with focus in a date field (the field moves focus to the next
 * end itself) or when the OTHER panel was pushed along. So a panel's days only take focus
 * while the panel already holds it — or on the mount that asked for it.
 */
const PanelFocus = createContext<MutableRefObject<boolean> | null>(null);

/**
 * A day of this panel is a button; a day of a neighbouring month is only its number. The
 * engine never makes an outside day its focus target, so the span is never asked to focus.
 */
function GridDayButton({ day: _day, modifiers, ...buttonProps }: DayButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const owned = useContext(PanelFocus);
  useEffect(() => {
    if (modifiers.focused && owned?.current) ref.current?.focus();
  }, [modifiers.focused, owned]);
  if (modifiers.outside) {
    return (
      <span className={buttonProps.className} aria-hidden>
        {buttonProps.children}
      </span>
    );
  }
  return <button ref={ref} {...buttonProps} />;
}

/** The engine's own caption is replaced by the panel header, which sits outside it. */
const NoCaption = () => null;

const COMPONENTS = { DayButton: GridDayButton, MonthCaption: NoCaption };

const MODIFIER_CLASSES = {
  cap_start: 'cal-cap-start',
  cap_end: 'cal-cap-end',
  cap_round: 'cal-cap-round',
  band: 'cal-band',
  preview: 'cal-preview',
  preview_end: 'cal-preview-end',
};

interface PanelProps {
  index: number;
  count: number;
  month: MonthIndex;
  limits: MonthLimits;
  onNavigate: (index: number, target: MonthIndex) => void;
  /**
   * The keyboard is about to leave this panel for `day`. Returns `true` when another panel
   * already shows that day and took focus — then this panel does not move.
   */
  onCrossInto: (index: number, day: IsoDate) => boolean;
  autoFocus: boolean;
  dayPicker: Record<string, unknown>;
}

function Panel({
  index,
  count,
  month,
  limits,
  onNavigate,
  onCrossInto,
  autoFocus,
  dayPicker,
}: PanelProps) {
  const { t, i18n } = useTranslation('dashboard', { keyPrefix: 'global' });
  const locale = intlLocale(i18n.language);
  const own = panelLimits(index, count, limits);
  const [view, setView] = useState<PickerView>(null);
  const [yearBase, setYearBase] = useState(() => Math.floor(month / 12) - 5);
  const owned = useRef(autoFocus);
  const monthButton = useRef<HTMLButtonElement>(null);
  const yearButton = useRef<HTMLButtonElement>(null);
  /** After a month or year is picked, focus returns to the name that opened the picker. */
  const refocus = useRef<'month' | 'year' | null>(null);

  const pickerRef = useRef<HTMLDivElement>(null);

  /* An opened picker takes focus on its current month or year — even from a click, which
     otherwise leaves focus in the date field — so Escape closes the picker, not the
     calendar, and the keyboard is already in it. A closed picker hands focus back to the
     name that opened it. */
  useEffect(() => {
    if (view !== null) {
      const current = pickerRef.current?.querySelector<HTMLElement>(
        'button[aria-current="true"]:not(:disabled)',
      );
      (current ?? pickerRef.current?.querySelector<HTMLElement>('button:not(:disabled)'))?.focus();
      return;
    }
    if (!refocus.current) return;
    (refocus.current === 'month' ? monthButton : yearButton).current?.focus();
    refocus.current = null;
  }, [view]);

  const date = fromMonthIndex(month);
  const year = date.getFullYear();
  const monthName = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
  const suffix = count === 2 ? `-${index}` : '';

  const navButton = (step: NavStep) => {
    const target = clampMonth(month + NAV_STEP[step], own);
    const Icon = NAV_ICON[step];
    return (
      <NavButton
        key={step}
        size='small'
        disableRipple
        disabled={target === month}
        aria-label={String(t(NAV_LABEL_KEY[step]))}
        onClick={() => {
          setView(null);
          onNavigate(index, target);
        }}
        data-testid={`calendar-${step}${suffix}`}
      >
        <Icon size={16} aria-hidden />
      </NavButton>
    );
  };

  const toggle = (next: Exclude<PickerView, null>) => {
    if (next === 'year') setYearBase(year - 5);
    if (view === next) refocus.current = next;
    setView(view === next ? null : next);
  };

  const choose = (target: MonthIndex, from: 'month' | 'year') => {
    refocus.current = from;
    setView(null);
    onNavigate(index, target);
  };

  /* A picker closes on Escape before the calendar does — wherever focus is in the panel,
     usually still on the month or year name that opened it. */
  const onPanelKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || view === null) return;
    event.stopPropagation();
    refocus.current = view;
    setView(null);
  };

  /* Arrow keys, Page Up/Down and Home/End that would carry focus into a month the other
     panel already shows move focus THERE, and neither panel moves (the spec demo's rule).
     Anything else is the engine's: it moves this panel to follow focus. */
  const onGridKeyDownCapture = (event: KeyboardEvent<HTMLElement>) => {
    const cell = (event.target as HTMLElement).closest<HTMLElement>('td[data-day]');
    const from = cell?.dataset.day;
    const next = from ? keyboardTarget(from, event.key, event.shiftKey) : null;
    if (next && isoMonth(next) !== month && onCrossInto(index, next)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const shortMonth = new Intl.DateTimeFormat(locale, { month: 'short' });
  const yearAllowed = (y: number) =>
    !(own.max !== null && y * 12 > own.max) && !(own.min !== null && y * 12 + 11 < own.min);

  let body;
  if (view === 'month') {
    body = (
      <PickerBody ref={pickerRef} data-testid={`calendar-month-picker${suffix}`}>
        <PickerGrid role='group' aria-label={String(t('calendar_choose_month'))}>
          {Array.from({ length: 12 }, (_, k) => {
            const target = year * 12 + k;
            return (
              <PickerButton
                key={k}
                disableRipple
                disabled={!withinLimits(target, own)}
                aria-current={target === month || undefined}
                onClick={() => choose(target, 'month')}
                data-testid={`calendar-month-option-${k + 1}`}
              >
                {shortMonth.format(fromMonthIndex(target)).replace('.', '')}
              </PickerButton>
            );
          })}
        </PickerGrid>
      </PickerBody>
    );
  } else if (view === 'year') {
    const years = Array.from({ length: 12 }, (_, k) => yearBase + k);
    body = (
      <PickerBody ref={pickerRef} data-testid={`calendar-year-picker${suffix}`}>
        <PickerGrid role='group' aria-label={String(t('calendar_choose_year'))}>
          {years.map((y) => (
            <PickerButton
              key={y}
              disableRipple
              disabled={!yearAllowed(y)}
              aria-current={y === year || undefined}
              onClick={() => choose(clampMonth(y * 12 + (month % 12), own), 'year')}
              data-testid={`calendar-year-option-${y}`}
            >
              {y}
            </PickerButton>
          ))}
        </PickerGrid>
        <PickerPaging>
          <PagingButton
            disableRipple
            disabled={!yearAllowed(yearBase - 1)}
            onClick={() => setYearBase((base) => base - 12)}
            data-testid='calendar-earlier-years'
          >
            {t('calendar_earlier_years')}
          </PagingButton>
          <PagingButton
            disableRipple
            disabled={!yearAllowed(yearBase + 12)}
            onClick={() => setYearBase((base) => base + 12)}
            data-testid='calendar-later-years'
          >
            {t('calendar_later_years')}
          </PagingButton>
        </PickerPaging>
      </PickerBody>
    );
  } else {
    body = (
      <PanelFocus.Provider value={owned}>
        <Box onKeyDownCapture={onGridKeyDownCapture}>
          <DayPicker
            {...dayPicker}
            month={date}
            onMonthChange={(next: Date) => onNavigate(index, toMonthIndex(next))}
            startMonth={own.min === null ? undefined : fromMonthIndex(own.min)}
            endMonth={own.max === null ? undefined : fromMonthIndex(own.max)}
            autoFocus={autoFocus}
          />
        </Box>
      </PanelFocus.Provider>
    );
  }

  return (
    <PanelBox
      data-panel={index}
      data-testid={`calendar-panel${suffix}`}
      onKeyDown={onPanelKeyDown}
      onFocus={(event) => {
        owned.current = event.currentTarget.contains(event.target as Node);
      }}
      onBlur={(event) => {
        // Only a move to somewhere ELSE gives the panel's focus up. Focus lost to nowhere —
        // the focused day unmounting as the keyboard crosses into the next month — is not.
        const next = event.relatedTarget;
        if (next instanceof Node && !event.currentTarget.contains(next)) owned.current = false;
      }}
    >
      <Header>
        <Nav>{[navButton('prevYear'), navButton('prevMonth')]}</Nav>
        <Title aria-live='polite'>
          <TitleButton
            ref={monthButton}
            disableRipple
            aria-expanded={view === 'month'}
            aria-label={`${monthName}, ${String(t('calendar_open_month_picker'))}`}
            onClick={() => toggle('month')}
            data-testid={`calendar-month-name${suffix}`}
          >
            {monthName}
          </TitleButton>
          <TitleButton
            ref={yearButton}
            className='calendar-year'
            disableRipple
            aria-expanded={view === 'year'}
            aria-label={`${year}, ${String(t('calendar_open_year_picker'))}`}
            onClick={() => toggle('year')}
            data-testid={`calendar-year-name${suffix}`}
          >
            {year}
          </TitleButton>
        </Title>
        <Nav>{[navButton('nextMonth'), navButton('nextYear')]}</Nav>
      </Header>
      {body}
    </PanelBox>
  );
}

export function CalendarGrid(props: CalendarGridProps) {
  const {
    onPick,
    months,
    onMonthsChange,
    minDate,
    maxDate,
    autoFocusPanel = null,
    'data-testid': testId,
  } = props;
  const { t, i18n } = useTranslation('dashboard', { keyPrefix: 'global' });
  const locale = intlLocale(i18n.language);
  /** The day under the pointer or keyboard focus — what a range preview runs to. */
  const [hovered, setHovered] = useState<IsoDate | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const limits = useMemo(() => monthLimits(minDate, maxDate), [minDate, maxDate]);
  const min = useMemo(() => isoToDate(minDate), [minDate]);
  const max = useMemo(() => isoToDate(maxDate), [maxDate]);
  const disabled = useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [];
    if (min) matchers.push({ before: min });
    if (max) matchers.push({ after: max });
    return matchers;
  }, [min, max]);

  const isRange = props.mode === 'range';
  const single = props.mode === 'single' ? props.value : null;
  const range = props.mode === 'range' ? props.value : null;
  const start = range?.start ?? null;
  const openEnded = props.mode === 'range' && Boolean(props.openEnded) && start !== null;
  const end = openEnded ? null : (range?.end ?? null);
  const active: Bound = props.mode === 'range' ? (props.activeBound ?? 'end') : 'start';
  const previewing =
    isRange &&
    active === 'end' &&
    start !== null &&
    hovered !== null &&
    compareIso(hovered, start) > 0;

  const modifiers = useMemo(() => {
    const iso = (date: Date) => dateToIso(date);
    if (!isRange) {
      return {
        cap_start: (date: Date) => iso(date) === single,
        cap_round: (date: Date) => iso(date) === single,
      };
    }
    const inBand = (day: IsoDate) =>
      start !== null && day > start && (openEnded || (end !== null && day < end));
    return {
      cap_start: (date: Date) => iso(date) === start,
      cap_end: (date: Date) => iso(date) === end,
      cap_round: (date: Date) => {
        const day = iso(date);
        if (day === start && day === end) return true;
        // A lone start (no end, no open end, nothing previewed) is a whole box; a lone end too.
        if (day === start) return end === null && !openEnded && !previewing;
        return day === end && start === null;
      },
      band: (date: Date) => !previewing && inBand(iso(date)),
      preview: (date: Date) => {
        const day = iso(date);
        return previewing && start !== null && hovered !== null && day > start && day < hovered;
      },
      preview_end: (date: Date) => previewing && iso(date) === hovered,
    };
  }, [isRange, single, start, end, openEnded, previewing, hovered]);

  const formatters = useMemo<Partial<Formatters>>(
    () => ({
      // Two letters, as the frame letters them: Mo Tu We / Mo Di Mi.
      formatWeekdayName: (date: Date) =>
        new Intl.DateTimeFormat(locale, { weekday: 'short' })
          .format(date)
          .replace('.', '')
          .slice(0, 2),
    }),
    [locale],
  );

  /** "Wednesday 1 January 2025, start date" — the full date and its role (spec *Keyboard*). */
  const labels = useMemo<Partial<Labels>>(() => {
    const full = new Intl.DateTimeFormat(locale, { dateStyle: 'full' });
    const monthYear = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
    return {
      labelGrid: (date: Date) => monthYear.format(date),
      labelWeekday: (date: Date) =>
        new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date),
      labelDayButton: (date, dayModifiers) => {
        const day = dateToIso(date);
        const parts = [full.format(date)];
        if (!isRange) {
          if (day === single) parts.push(String(t('calendar_selected')));
        } else {
          if (day === start) parts.push(String(t('calendar_day_start')));
          if (day === end) parts.push(String(t('calendar_day_end')));
          if (start !== null && day > start && (openEnded || (end !== null && day < end))) {
            parts.push(String(t('calendar_day_in_range')));
          }
        }
        if (dayModifiers.today) parts.push(String(t('calendar_today')));
        return parts.join(', ');
      },
    };
  }, [locale, t, isRange, single, start, end, openEnded]);

  const navigate = (index: number, target: MonthIndex) =>
    onMonthsChange(movePanel(months, index, target, limits));

  const crossInto = (index: number, day: IsoDate): boolean => {
    if (!isWithinBounds(day, minDate, maxDate)) return false;
    const other = months.findIndex((month, i) => i !== index && month === isoMonth(day));
    if (other < 0) return false;
    const button = rootRef.current?.querySelector<HTMLElement>(
      `[data-panel="${other}"] td[data-day="${day}"]:not([data-outside]) button`,
    );
    if (!button) return false;
    button.focus();
    return true;
  };

  const selection = isRange
    ? {
        mode: 'range' as const,
        required: true,
        selected: start
          ? { from: isoToDate(start) ?? undefined, to: isoToDate(end) ?? undefined }
          : undefined,
      }
    : { mode: 'single' as const, required: true, selected: isoToDate(single) ?? undefined };

  const dayPicker = {
    ...selection,
    // The pick is read from the click; the engine's own idea of the next range is ignored —
    // what a click sets depends on the active end, which the engine cannot know.
    onSelect: () => undefined,
    onDayClick: (date: Date) => onPick(dateToIso(date)),
    numberOfMonths: 1,
    weekStartsOn: 1 as const,
    showOutsideDays: true,
    fixedWeeks: true,
    hideNavigation: true,
    disabled,
    modifiers,
    modifiersClassNames: MODIFIER_CLASSES,
    components: COMPONENTS,
    formatters,
    labels,
    lang: locale,
    onDayMouseEnter: (date: Date) => setHovered(dateToIso(date)),
    onDayFocus: (date: Date) => setHovered(dateToIso(date)),
    onDayMouseLeave: () => setHovered(null),
  };

  return (
    <Panels ref={rootRef} data-testid={testId}>
      {months.map((month, index) => (
        <Panel
          key={index}
          index={index}
          count={months.length}
          month={month}
          limits={limits}
          onNavigate={navigate}
          onCrossInto={crossInto}
          autoFocus={autoFocusPanel === index}
          dayPicker={dayPicker}
        />
      ))}
    </Panels>
  );
}
